import { useEffect } from "react";
import { Form, Row, Col, Select, InputNumber, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";

export default function PricingYieldConfigurationForm({
  onFinish,
  initialValues,
  form,
  pricingMethods,
  quotationTypeOptions,
  yieldBasisOptions,
  cleanDirtyPriceFlagOptions,
  valuationCurveTypeOptions,
  bondCouponFrequencies,
  productCategory, // 👈 add this
}) {
  const isDiscount = productCategory === 0; // T-Bill
  const isFixedIncome = productCategory === 1; // Bond
  const isMoneyMarket = productCategory === 2; // Interbank

  useEffect(() => {
    if (initialValues) form.setFieldsValue(initialValues);
  }, [initialValues, form]);

  return (
    <div>
      <h3 style={{ marginBottom: 24, fontWeight: 600 }}>
        Pricing & Yield Configuration
      </h3>

      <Form layout="vertical" form={form} onFinish={onFinish}>
        {/* Row 1: Pricing Method & Quotation Type — always shown */}
        <Row>
          <Col span={12} style={{ padding: "0 8px" }}>
            <Form.Item
              label={
                <span>
                  Pricing Method
                  <Tooltip title="Determines which input is mandatory at deal booking and how the system derives the corresponding price or yield.">
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="pricingMethod"
              rules={[{ required: true }]}
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={pricingMethods}
              />
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0 8px" }}>
            <Form.Item
              label={
                <span>
                  Quotation Type
                  <Tooltip title="Defines how traders quote and interpret market rates and how the system converts them into valuation inputs.">
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="quotationType"
              rules={[{ required: true }]}
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={quotationTypeOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 2: Yield Basis — always shown | Discount Basis — T-Bill only */}
        <Row>
          <Col span={12} style={{ padding: "0 8px" }}>
            <Form.Item
              label={
                <span>
                  Yield Basis
                  <Tooltip title="Controls yield-to-price conversion formulas and compounding logic.">
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="yieldBasis"
              rules={[{ required: true }]}
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={yieldBasisOptions}
              />
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0 8px" }}>
            <Form.Item
              label={
                <span>
                  Valuation Curve Type
                  <Tooltip title="Controls which yield curve is used for valuation and mark-to-market calculations.">
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="valuationCurveType"
              rules={!isMoneyMarket ? [{ required: true }] : []}
            >
              <Select
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" />}
                options={valuationCurveTypeOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 3: Price Decimals & Yield Decimals — always shown */}
        <Row>
          <Col span={12} style={{ padding: "0 8px" }}>
            <Form.Item
              label={
                <span>
                  Price Decimals
                  <Tooltip title="Enforces rounding precision for prices across booking, valuation, and reporting.">
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="priceDecimals"
              rules={[{ required: true }]}
            >
              <InputNumber
                style={{ width: "100%", height: 44 }}
                min={0}
                max={10}
              />
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0 8px" }}>
            <Form.Item
              label={
                <span>
                  Yield Decimals
                  <Tooltip title="Enforces consistent yield rounding and avoids pricing discrepancies.">
                    <InfoCircleOutlined
                      style={{ marginLeft: 4, color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="yieldDecimals"
              rules={[{ required: true }]}
            >
              <InputNumber
                style={{ width: "100%", height: 44 }}
                min={0}
                max={10}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          {/* Clean/Dirty: Bond only */}
          {isFixedIncome && (
            <Col span={12} style={{ padding: "0 8px" }}>
              <Form.Item
                label={
                  <span>
                    Clean / Dirty Price
                    <Tooltip title="Determines whether accrued interest is included in quoted prices or calculated separately.">
                      <InfoCircleOutlined
                        style={{ marginLeft: 4, color: "#667085" }}
                      />
                    </Tooltip>
                  </span>
                }
                name="cleanDirtyPriceFlag"
              >
                <Select
                  style={{ height: 44 }}
                  suffixIcon={<ChevronDown color="#667085" />}
                  options={cleanDirtyPriceFlagOptions}
                />
              </Form.Item>
            </Col>
          )}

          {isDiscount && (
            <Col span={12} style={{ padding: "0 8px" }}>
              <Form.Item
                label={
                  <span>
                    Discount Basis
                    <Tooltip title="Defines the denominator used in discount pricing formulas for discount instruments.">
                      <InfoCircleOutlined
                        style={{ marginLeft: 4, color: "#667085" }}
                      />
                    </Tooltip>
                  </span>
                }
                name="discountBasis"
              >
                <Select
                  style={{ height: 44 }}
                  suffixIcon={<ChevronDown color="#667085" />}
                  options={bondCouponFrequencies}
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        <button type="submit" hidden />
      </Form>
    </div>
  );
}
